// frontend/src/utils/foutmelding.ts
//
// B12 / besluit Q13 (12-10-2026): één plek die van een fout een melding voor
// het scherm maakt, in plaats van losse kopieën per module.
//
// - hoofdtekst: de Nederlandse melding van de server als het antwoord er een
//   heeft (Nest: `message` als string, of als lijst van class-validator, met
//   "; " aan elkaar). Anders (serverfout zonder eigen melding, geen verbinding,
//   onbekende fout) de algemene tekst.
// - technisch: platte tekst om te kopiëren: tijd, verzoek, status,
//   servermelding en bij een fout zonder HTTP-antwoord de naam en melding.
//   Alleen het pad van de URL, nooit de query (daar kunnen tokens in staan).

export const ALGEMENE_FOUTTEKST =
  "Hier gaat iets mis! Kopieer dit en app of mail naar Niels.";

export interface Foutmelding {
  hoofdtekst: string;
  technisch: string;
}

/** True voor een object uit `maakFoutmelding` (naast een losse tekst of JSX). */
export function isFoutmelding(waarde: unknown): waarde is Foutmelding {
  return (
    !!waarde &&
    typeof waarde === "object" &&
    typeof (waarde as Foutmelding).hoofdtekst === "string" &&
    typeof (waarde as Foutmelding).technisch === "string"
  );
}

/**
 * Een fout waarvan de tekst al voor de gebruiker is geschreven (bv. "Het
 * bestand is in gebruik."). `maakFoutmelding` neemt die tekst als hoofdtekst;
 * een gewone Error geldt als technisch en krijgt de algemene tekst.
 */
export class Gebruikersfout extends Error {
  constructor(tekst: string, opties?: { cause?: unknown }) {
    super(tekst, opties);
    this.name = "Gebruikersfout";
  }
}

interface HttpFoutVorm {
  config?: { method?: unknown; url?: unknown };
  response?: { status?: unknown; statusText?: unknown; data?: unknown };
  code?: unknown;
}

const TIJD = new Intl.DateTimeFormat("nl-NL", {
  timeZone: "Europe/Amsterdam",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

/** dd-mm-yyyy hh:mm:ss in Nederlandse tijd. */
export function formatFoutTijd(moment: Date): string {
  const d: Record<string, string> = {};
  for (const deel of TIJD.formatToParts(moment)) d[deel.type] = deel.value;
  return `${d.day}-${d.month}-${d.year} ${d.hour}:${d.minute}:${d.second}`;
}

/**
 * Standaardteksten van Nest zelf (Engels, zonder inhoud voor de gebruiker):
 * die tellen niet als servermelding voor de hoofdtekst.
 */
const STANDAARD_MELDINGEN = new Set([
  "Bad Request",
  "Unauthorized",
  "Forbidden",
  "Forbidden resource",
  "Not Found",
  "Method Not Allowed",
  "Not Acceptable",
  "Request Timeout",
  "Conflict",
  "Gone",
  "Payload Too Large",
  "Unsupported Media Type",
  "Unprocessable Entity",
  "Too Many Requests",
  "ThrottlerException: Too Many Requests",
  "Internal server error",
  "Internal Server Error",
  "Not Implemented",
  "Bad Gateway",
  "Service Unavailable",
  "Gateway Timeout",
]);

/**
 * Een gearchiveerd dossier: de map staat in SharePoint alleen open voor
 * dossierbeheer (19-10-2026). Ook de regel boven de documenten van zo'n dossier.
 */
export const ARCHIEF_SHAREPOINT_TEKST =
  "Gearchiveerd dossier: openen in SharePoint kan alleen voor dossierbeheer. Bekijken en downloaden in de webapp werkt wel.";

/**
 * Vaste codes die de server als melding geeft, met de zin voor het scherm
 * (fixronde WP2). De code zelf blijft in het technische blok staan.
 */
const SERVERCODES = new Map<string, string>([
  [
    "beeldvorming_niet_beschikbaar",
    "De beeldvorming is in de webapp niet beschikbaar.",
  ],
  [
    "dossier_in_archief",
    "Dit dossier staat in het archief. Toon in OneDrive werkt alleen bij een actief dossier.",
  ],
  ["dossiermap_ontbreekt", "Dit dossier heeft nog geen map in SharePoint."],
  // Openen in SharePoint op een gearchiveerd dossier (19-10-2026).
  ["sharepoint_alleen_dossierbeheer", ARCHIEF_SHAREPOINT_TEKST],
  // WP4 fixronde 2: codes die ook bij samenvoegen terugkomen.
  [
    "invoice_folder_access_denied",
    "De facturen zijn alleen voor beheer en financiën.",
  ],
  ["prullenbak_geen_doel", "In de prullenbak kan geen nieuw document komen."],
  // Trein 50 (B3): samenvoegen, één tegelijk en een paginalimiet.
  [
    "samenvoegen_bezig",
    "Er wordt al een pdf samengevoegd. Probeer het zo opnieuw.",
  ],
  ["samenvoegen_te_veel_paginas", "Samen hebben de pdf's te veel pagina's."],
  // Kennisbank (26-10-2026): het bestand is intussen weg of verplaatst.
  [
    "item_niet_gevonden",
    "Dit bestand staat hier niet meer. Klik op Vernieuw en probeer het opnieuw.",
  ],
  // Trein 50, ronde 2: de twee 409's van het vastleggen van een document (B7).
  [
    "document_in_ander_dossier",
    "Dit bestand hoort bij een ander dossier. Vraag beheer om het recht te zetten.",
  ],
  [
    "document_rij_verdwenen",
    "Het bestand staat in SharePoint, maar nog niet in de lijst. Vernieuw de pagina.",
  ],
  // WP5 (prullenbak): de code staat in het veld `code`, naast een eigen melding.
  [
    "prullenbak_pad_onbekend",
    "Het is nu niet na te gaan waar dit bestand staat. Er is niets gewijzigd; probeer het later opnieuw.",
  ],
  [
    "factuurmap_slot_mislukt",
    "De factuurmap kon niet worden afgeschermd. Er is niets verplaatst; probeer het later opnieuw.",
  ],
  // WP5 fixronde 6: twee 400-codes die de server als melding geeft.
  [
    "target_folder_not_found",
    "De doelmap bestaat niet meer in SharePoint. Vernieuw de pagina en probeer het opnieuw.",
  ],
  [
    "sp_dev_mode_alleen_zandbak",
    "Op de lokale testomgeving kan dit alleen in de testmap.",
  ],
  // WP5: verwijderen naar de prullenbak geldt alleen voor een bestand.
  [
    "alleen_bestanden",
    "Dit is een map. Alleen bestanden kunnen hier worden verwijderd.",
  ],
  // WP3 (uploads): codes die in de foutenlijst van een upload kunnen komen.
  [
    "bestand_niet_gevonden",
    "Het bestand staat niet (meer) in SharePoint. Upload het opnieuw.",
  ],
  // Het bestand staat er al: niet uitnodigen tot een tweede upload (B3, low 6).
  ["bestand_al_vastgelegd", "Dit bestand staat al in het dossier."],
  [
    "size_mismatch",
    "Het bestand in SharePoint is niet even groot als het geüploade bestand. Upload het opnieuw.",
  ],
  [
    "head_failed",
    "SharePoint gaf de gegevens van het bestand niet. Probeer het opnieuw.",
  ],
  ["upload_batch_gestopt", "Deze upload is gestopt."],
  [
    "upload_batch_niet_gevonden",
    "Deze upload bestaat niet meer. Begin hem opnieuw.",
  ],
  [
    "upload_bestand_niet_gevonden",
    "Dit bestand hoort niet meer bij deze upload.",
  ],
]);

/** De zin voor het scherm bij een bekende servercode, of null. */
export function tekstVoorServercode(
  code: string | null | undefined,
): string | null {
  return (code && SERVERCODES.get(code)) || null;
}

function isStandaardMelding(melding: string): boolean {
  return (
    STANDAARD_MELDINGEN.has(melding) ||
    /^Cannot (GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS) /.test(melding) ||
    /^Validation failed\b/.test(melding)
  );
}

function isHttpFout(fout: unknown): fout is HttpFoutVorm {
  if (!fout || typeof fout !== "object") return false;
  const f = fout as {
    isAxiosError?: unknown;
    response?: unknown;
    config?: unknown;
  };
  return (
    f.isAxiosError === true ||
    (typeof f.response === "object" && f.response !== null) ||
    (typeof f.config === "object" && f.config !== null)
  );
}

/** De fout zelf of een fout in de `cause`-keten (services die een fout inpakken). */
function vindHttpFout(fout: unknown): HttpFoutVorm | null {
  let huidig: unknown = fout;
  for (let i = 0; i < 5 && huidig; i++) {
    if (isHttpFout(huidig)) return huidig;
    huidig =
      typeof huidig === "object" ? (huidig as { cause?: unknown }).cause : null;
  }
  return null;
}

/** De `message` uit een Nest-foutantwoord, ongeacht of het Engels of Nederlands is. */
function leesMelding(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const bericht = (data as { message?: unknown }).message;
  if (Array.isArray(bericht)) {
    const delen = bericht
      .filter((d): d is string => typeof d === "string")
      .map((d) => d.trim())
      .filter(Boolean);
    return delen.length ? delen.join("; ") : null;
  }
  return typeof bericht === "string" && bericht.trim() ? bericht.trim() : null;
}

/**
 * Voor services met fetch: een Error met dezelfde vorm als een axios-fout
 * (status, antwoord, verzoek), zodat `maakFoutmelding` hem net zo leest. De
 * message blijft wat de oude code gooide: de servermelding of `terugval`.
 */
export function fetchFout(
  antwoord: Response,
  data: unknown,
  terugval: string,
  methode?: string,
): Error {
  return Object.assign(new Error(leesMelding(data) ?? terugval), {
    response: {
      status: antwoord.status,
      statusText: antwoord.statusText,
      data,
    },
    config: { method: methode, url: antwoord.url },
  });
}

/** Het veld `code` uit een Nest-foutantwoord (WP5 zet de code daar), of null. */
function leesCode(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const code = (data as { code?: unknown }).code;
  return typeof code === "string" && code ? code : null;
}

/**
 * De zin voor het scherm uit het antwoord: eerst een bekende code in `code`,
 * dan de melding zelf (een bekende code als melding wordt ook een zin).
 */
function serverZin(data: unknown): string | null {
  const opCode = tekstVoorServercode(leesCode(data));
  if (opCode) return opCode;
  const melding = leesMelding(data);
  return melding && !isStandaardMelding(melding)
    ? (tekstVoorServercode(melding) ?? melding)
    : null;
}

/** De servermelding die als hoofdtekst mag dienen, of null. */
export function leesServerMelding(fout: unknown): string | null {
  return serverZin(vindHttpFout(fout)?.response?.data);
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Alleen het pad; lange losse stukken (tokens, codes) gemaskeerd. */
function veiligPad(url: unknown): string | null {
  if (typeof url !== "string" || !url) return null;
  let pad = url;
  try {
    pad = new URL(url, "http://x").pathname;
  } catch {
    pad = url.split(/[?#]/)[0];
  }
  return pad
    .split("/")
    .map((stuk) => (stuk.length >= 24 && !UUID.test(stuk) ? "…" : stuk))
    .join("/");
}

function naamEnMelding(fout: unknown): string | null {
  if (fout instanceof Error) {
    return fout.message ? `${fout.name}: ${fout.message}` : fout.name;
  }
  if (typeof fout === "string") return fout || null;
  if (fout && typeof fout === "object") {
    const f = fout as { name?: unknown; message?: unknown };
    if (typeof f.message === "string" && f.message) {
      return typeof f.name === "string" && f.name
        ? `${f.name}: ${f.message}`
        : f.message;
    }
  }
  return null;
}

function serverAntwoord(data: unknown): string | null {
  if (typeof data === "string") {
    const tekst = data.trim();
    if (!tekst) return null;
    if (tekst.startsWith("<")) return "HTML-pagina (niet overgenomen)";
    const kort = tekst.replace(/\s+/g, " ");
    return kort.length > 200 ? `${kort.slice(0, 200)}…` : kort;
  }
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    return `bestand (${data.type || "onbekend type"})`;
  }
  return null;
}

/** Eerste Gebruikersfout in de `cause`-keten. */
function vindGebruikersfout(fout: unknown): Gebruikersfout | null {
  let huidig: unknown = fout;
  for (let i = 0; i < 5 && huidig; i++) {
    if (huidig instanceof Gebruikersfout) return huidig;
    huidig =
      typeof huidig === "object" ? (huidig as { cause?: unknown }).cause : null;
  }
  return null;
}

/**
 * Maakt van een fout (axios, fetch-fout, ingepakte fout met `cause`, Error,
 * string of iets onbekends) een melding voor het scherm. `eigenTekst`: een
 * bewuste eigen zin van de aanroeper; die blijft de hoofdtekst, het
 * technische blok komt erbij.
 */
export function maakFoutmelding(
  fout: unknown,
  eigenTekst?: string,
  moment: Date = new Date(),
): Foutmelding {
  const regels = [`Tijd: ${formatFoutTijd(moment)}`];
  if (eigenTekst) regels.push(`Melding: ${eigenTekst}`);

  const http = vindHttpFout(fout);
  const melding = leesMelding(http?.response?.data);

  if (http) {
    const methode =
      typeof http.config?.method === "string"
        ? http.config.method.toUpperCase()
        : null;
    const pad = veiligPad(http.config?.url);
    if (methode || pad) {
      regels.push(`Verzoek: ${[methode, pad].filter(Boolean).join(" ")}`);
    }
    const status = http.response?.status;
    if (typeof status === "number") {
      const tekst =
        typeof http.response?.statusText === "string"
          ? http.response.statusText
          : "";
      regels.push(`Status: ${`${status} ${tekst}`.trim()}`);
    } else {
      const code = typeof http.code === "string" ? ` (${http.code})` : "";
      regels.push(`Status: geen antwoord van de server${code}`);
    }
    if (melding) {
      regels.push(`Servermelding: ${melding}`);
    } else {
      const antwoord = serverAntwoord(http.response?.data);
      if (antwoord) regels.push(`Serverantwoord: ${antwoord}`);
    }
  }

  // Zonder HTTP-antwoord (geen verbinding, fout in de browser) of als een
  // service de fout heeft ingepakt: naam en melding van de fout zelf.
  if (!http || !http.response || fout !== http) {
    const naam = naamEnMelding(fout);
    regels.push(`Fout: ${naam ?? "onbekend (geen gegevens)"}`);
  }

  const serverMelding = serverZin(http?.response?.data);
  return {
    hoofdtekst:
      eigenTekst ||
      vindGebruikersfout(fout)?.message ||
      serverMelding ||
      ALGEMENE_FOUTTEKST,
    technisch: regels.join("\n"),
  };
}

/** Dezelfde melding met een eigen begin, bv. "Fout bij toevoegen medewerker: ". */
export function metVoorvoegsel(
  voorvoegsel: string,
  melding: Foutmelding,
): Foutmelding {
  return { ...melding, hoofdtekst: `${voorvoegsel}${melding.hoofdtekst}` };
}

/**
 * Meerdere fouten (bv. per bestand) in één melding. Zelfde hoofdtekst overal:
 * de namen en die tekst één keer; anders per naam de eigen hoofdtekst.
 */
export function bundelFoutmeldingen(
  kop: string,
  delen: { naam: string; melding: Foutmelding }[],
): Foutmelding {
  const teksten = Array.from(new Set(delen.map((d) => d.melding.hoofdtekst)));
  const hoofdtekst =
    teksten.length === 1
      ? `${kop} ${delen.map((d) => d.naam).join(", ")}. ${teksten[0]}`
      : `${kop} ${delen
          .map((d) => `${d.naam}: ${d.melding.hoofdtekst}`)
          .join("; ")}`;
  return {
    hoofdtekst,
    technisch: delen
      .map((d) => `[${d.naam}]\n${d.melding.technisch}`)
      .join("\n\n"),
  };
}
